import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
    ArrowLeft,
    FolderKanban,
    UploadCloud,
    FileText,
    FileSpreadsheet,
    FileCode,
    FileArchive,
    X,
    Loader2,
    CheckCircle2,
    AlertCircle,
    Info
} from 'lucide-react';
import { toast } from 'react-hot-toast';

export default function CreateProject() {
    const navigate = useNavigate();
    const [cohorts, setCohorts] = useState<any[]>([]);
    const [loadingCohorts, setLoadingCohorts] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [formData, setFormData] = useState({
        title: '',
        cohort_id: '',
        description: ''
    });

    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [dragActive, setDragActive] = useState(false);

    const API_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';

    const ALLOWED_EXTENSIONS = [
        'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'csv', 'txt', 'zip', 'rar', '7z'
    ];

    useEffect(() => {
        const fetchCohorts = async () => {
            try {
                const response = await fetch(`${API_URL}/cohorts`, {
                    headers: {
                        'Accept': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                });
                if (response.ok) {
                    const data = await response.json();
                    setCohorts(data);
                    if (data.length > 0) {
                        setFormData(prev => ({ ...prev, cohort_id: data[0].id }));
                    }
                }
            } catch (err) {
                console.error('Failed to load cohorts:', err);
                toast.error('Could not load cohorts list.');
            } finally {
                setLoadingCohorts(false);
            }
        };

        fetchCohorts();
    }, []);

    const validateAndSetFile = (file: File) => {
        const ext = file.name.split('.').pop()?.toLowerCase() || '';

        if (!ALLOWED_EXTENSIONS.includes(ext)) {
            toast.error(`Invalid file format (.${ext}). Supported formats: ${ALLOWED_EXTENSIONS.join(', ')}`);
            return false;
        }

        setSelectedFile(file);
        return true;
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            validateAndSetFile(e.target.files[0]);
        }
    };

    const handleDrag = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            validateAndSetFile(e.dataTransfer.files[0]);
        }
    };

    const formatFileSize = (bytes: number) => {
        if (bytes < 1024 * 1024) {
            return (bytes / 1024).toFixed(1) + ' KB';
        }
        return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
    };

    const getFileIcon = (filename: string) => {
        const ext = filename.split('.').pop()?.toLowerCase() || '';
        if (['xls', 'xlsx', 'csv'].includes(ext)) {
            return <FileSpreadsheet className="text-emerald-600" size={32} />;
        }
        if (ext === 'pdf') {
            return <FileText className="text-red-500" size={32} />;
        }
        if (['doc', 'docx'].includes(ext)) {
            return <FileText className="text-blue-500" size={32} />;
        }
        if (['ppt', 'pptx'].includes(ext)) {
            return <FileText className="text-orange-500" size={32} />;
        }
        if (['zip', 'rar', '7z'].includes(ext)) {
            return <FileArchive className="text-purple-500" size={32} />;
        }
        return <FileCode className="text-slate-600" size={32} />;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.title.trim()) {
            toast.error('Please enter a project title.');
            return;
        }

        if (!formData.cohort_id) {
            toast.error('Please select a target cohort.');
            return;
        }

        if (!selectedFile) {
            toast.error('Please attach a project file.');
            return;
        }

        setIsSubmitting(true);
        const loadingToast = toast.loading('Uploading project file...');

        try {
            const data = new FormData();
            data.append('title', formData.title.trim());
            data.append('cohort_id', formData.cohort_id);
            if (formData.description.trim()) {
                data.append('description', formData.description.trim());
            }
            data.append('project_file', selectedFile);

            const response = await fetch(`${API_URL}/instructor/projects`, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: data
            });

            const result = await response.json();

            if (response.ok) {
                toast.success('Project uploaded and published successfully!', { id: loadingToast });
                navigate('/instructor/projects');
            } else {
                throw new Error(result.message || 'Failed to create project.');
            }
        } catch (err: any) {
            console.error('Project Upload Error:', err);
            toast.error(err.message || 'An error occurred while uploading the project.', { id: loadingToast });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="create-project-container">
            <style>{`
                .create-project-container,
                .staff-scope .create-project-container {
                    max-width: 860px;
                    margin: 0 auto;
                    font-family: 'Inter', sans-serif;
                    padding: 1rem 1.25rem 4rem 1.25rem;
                    box-sizing: border-box;
                    width: 100%;
                }

                .form-card,
                .staff-scope .form-card {
                    background: var(--index-card-bg);
                    border: 1px solid var(--index-border-color);
                    border-radius: 24px;
                    padding: 2.25rem;
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.03);
                    box-sizing: border-box;
                    width: 100%;
                }

                .form-group,
                .staff-scope .form-group {
                    margin-bottom: 1.5rem;
                }

                .form-label,
                .staff-scope .form-label {
                    display: block;
                    font-size: 0.92rem;
                    font-weight: 800;
                    color: var(--index-text-heading);
                    margin-bottom: 0.5rem;
                }

                .form-label .required-dot,
                .staff-scope .form-label .required-dot {
                    color: #ef4444;
                    margin-left: 3px;
                }

                .form-input,
                .form-select,
                .form-textarea,
                .staff-scope .form-input,
                .staff-scope .form-select,
                .staff-scope .form-textarea {
                    width: 100%;
                    padding: 0.85rem 1.15rem;
                    border: 1px solid var(--index-border-color);
                    border-radius: 14px;
                    font-size: 0.95rem;
                    font-weight: 600;
                    background: var(--index-hover-bg);
                    color: var(--index-text-heading);
                    outline: none;
                    transition: all 0.2s;
                    box-sizing: border-box;
                }

                .form-input:focus,
                .form-select:focus,
                .form-textarea:focus,
                .staff-scope .form-input:focus,
                .staff-scope .form-select:focus,
                .staff-scope .form-textarea:focus {
                    border-color: var(--index-primary-color);
                    background: var(--index-card-bg);
                    box-shadow: 0 0 0 3px color-mix(in srgb, var(--index-primary-color) 15%, transparent);
                }

                .upload-dropzone,
                .staff-scope .upload-dropzone {
                    border: 2px dashed var(--index-border-color);
                    border-radius: 18px;
                    padding: 2.25rem 1.25rem;
                    text-align: center;
                    background: var(--index-hover-bg);
                    transition: all 0.2s ease;
                    cursor: pointer;
                    position: relative;
                }

                .upload-dropzone.active,
                .staff-scope .upload-dropzone.active {
                    border-color: var(--index-primary-color);
                    background: rgba(16, 185, 129, 0.05);
                }

                .upload-dropzone:hover,
                .staff-scope .upload-dropzone:hover {
                    border-color: var(--index-primary-color);
                }

                .file-selected-card,
                .staff-scope .file-selected-card {
                    background: var(--index-card-bg);
                    border: 1px solid var(--index-border-color);
                    border-radius: 16px;
                    padding: 1.15rem;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.02);
                    gap: 0.75rem;
                    min-width: 0;
                }

                .btn-submit-action,
                .staff-scope .btn-submit-action {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    background: var(--index-primary-color);
                    color: white;
                    padding: 0.85rem 2rem;
                    min-height: 46px;
                    border-radius: 14px;
                    font-weight: 800;
                    font-size: 0.95rem;
                    border: none;
                    cursor: pointer;
                    transition: all 0.2s;
                    box-shadow: 0 8px 16px -3px color-mix(in srgb, var(--index-primary-color) 30%, transparent);
                }

                .btn-submit-action:hover:not(:disabled),
                .staff-scope .btn-submit-action:hover:not(:disabled) {
                    transform: translateY(-2px);
                    box-shadow: 0 12px 20px -3px color-mix(in srgb, var(--index-primary-color) 40%, transparent);
                }

                .btn-submit-action:disabled,
                .staff-scope .btn-submit-action:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }

                .form-actions-bar,
                .staff-scope .form-actions-bar {
                    display: flex;
                    align-items: center;
                    justify-content: flex-end;
                    gap: 0.75rem;
                    padding-top: 1.5rem;
                    border-top: 1px solid var(--index-border-color);
                }

                .btn-cancel-action,
                .staff-scope .btn-cancel-action {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    padding: 0.85rem 1.5rem;
                    min-height: 46px;
                    border-radius: 14px;
                    border: 1px solid var(--index-border-color);
                    background: var(--index-card-bg);
                    font-size: 0.95rem;
                    font-weight: 700;
                    color: var(--index-text-secondary);
                    text-decoration: none;
                    transition: all 0.2s;
                }

                .btn-cancel-action:hover,
                .staff-scope .btn-cancel-action:hover {
                    background: var(--index-hover-bg);
                    color: var(--index-text-heading);
                }

                @media (max-width: 768px) {
                    .create-project-container,
                    .staff-scope .create-project-container {
                        padding: 0.75rem 0.75rem 3rem 0.75rem;
                    }

                    .form-card,
                    .staff-scope .form-card {
                        padding: 1.25rem 1rem;
                        border-radius: 18px;
                    }

                    .form-header-box,
                    .staff-scope .form-header-box {
                        gap: 0.75rem !important;
                        margin-bottom: 1.25rem !important;
                        padding-bottom: 1.25rem !important;
                    }

                    .form-header-box h1,
                    .staff-scope .form-header-box h1 {
                        font-size: 1.35rem !important;
                    }

                    .form-header-box p,
                    .staff-scope .form-header-box p {
                        font-size: 0.82rem !important;
                    }

                    .form-input,
                    .form-select,
                    .form-textarea,
                    .staff-scope .form-input,
                    .staff-scope .form-select,
                    .staff-scope .form-textarea {
                        font-size: 1rem; /* Prevents iOS auto-zoom */
                    }

                    .upload-dropzone,
                    .staff-scope .upload-dropzone {
                        padding: 1.5rem 0.75rem;
                    }

                    .upload-dropzone h4,
                    .staff-scope .upload-dropzone h4 {
                        font-size: 0.92rem !important;
                    }

                    .file-selected-card,
                    .staff-scope .file-selected-card {
                        padding: 0.85rem;
                    }

                    .form-actions-bar,
                    .staff-scope .form-actions-bar {
                        flex-direction: column-reverse;
                        gap: 0.65rem;
                        width: 100%;
                    }

                    .btn-cancel-action,
                    .btn-submit-action,
                    .staff-scope .btn-cancel-action,
                    .staff-scope .btn-submit-action {
                        width: 100%;
                        justify-content: center;
                    }
                }
            `}</style>

            {/* Back Navigation */}
            <div className="mb-5">
                <Link
                    to="/instructor/projects"
                    className="inline-flex items-center gap-2 text-sm font-bold text-gray-500 hover:text-brand-emerald transition-colors"
                >
                    <ArrowLeft size={16} />
                    <span>Back to Projects</span>
                </Link>
            </div>

            <div className="form-card">
                {/* Header */}
                <div className="form-header-box flex items-center gap-3.5 mb-6 pb-6 border-b border-gray-100 dark:border-gray-800">
                    <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 text-brand-emerald flex items-center justify-center flex-shrink-0">
                        <FolderKanban size={22} />
                    </div>
                    <div>
                        <h1 className="text-xl sm:text-2xl font-black text-gray-900 dark:text-white m-0">Create New Project</h1>
                        <p className="text-gray-500 dark:text-gray-400 text-xs sm:text-sm font-semibold m-0 mt-0.5">
                            Upload a workbook, scheduling exercise, or document for your cohort.
                        </p>
                    </div>
                </div>

                <form onSubmit={handleSubmit}>
                    {/* Project Title */}
                    <div className="form-group">
                        <label className="form-label" htmlFor="title">
                            Project Title <span className="required-dot">*</span>
                        </label>
                        <input
                            id="title"
                            type="text"
                            className="form-input"
                            placeholder="e.g. Primavera P6 Scheduling Baseline Project"
                            value={formData.title}
                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                            required
                        />
                    </div>

                    {/* Target Cohort */}
                    <div className="form-group">
                        <label className="form-label" htmlFor="cohort_id">
                            Target Cohort <span className="required-dot">*</span>
                        </label>
                        {loadingCohorts ? (
                            <div className="flex items-center gap-2 p-3 text-sm text-gray-400 font-semibold bg-gray-50 rounded-xl">
                                <Loader2 size={16} className="animate-spin text-brand-emerald" />
                                <span>Loading cohorts...</span>
                            </div>
                        ) : cohorts.length === 0 ? (
                            <div className="p-3 text-sm text-amber-700 font-semibold bg-amber-50 rounded-xl border border-amber-200">
                                No cohorts available. Please create a cohort first.
                            </div>
                        ) : (
                            <select
                                id="cohort_id"
                                className="form-select"
                                value={formData.cohort_id}
                                onChange={(e) => setFormData({ ...formData, cohort_id: e.target.value })}
                                required
                            >
                                {cohorts.map((cohort: any) => (
                                    <option key={cohort.id} value={cohort.id}>
                                        {cohort.name} ({cohort.course?.title || 'General'})
                                    </option>
                                ))}
                            </select>
                        )}
                        <p className="text-xs text-gray-400 font-semibold mt-1.5 flex items-center gap-1">
                            <Info size={13} />
                            <span>All students enrolled in this cohort will have access to download this project.</span>
                        </p>
                    </div>

                    {/* Description */}
                    <div className="form-group">
                        <label className="form-label" htmlFor="description">
                            Description <span className="text-xs font-normal text-gray-400">(Optional)</span>
                        </label>
                        <textarea
                            id="description"
                            rows={3}
                            className="form-textarea"
                            placeholder="Provide project guidelines, objectives, or instructions for students..."
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        />
                    </div>

                    {/* Project File Upload */}
                    <div className="form-group">
                        <label className="form-label">
                            Project File <span className="required-dot">*</span>
                        </label>

                        {!selectedFile ? (
                            <div
                                className={`upload-dropzone ${dragActive ? 'active' : ''}`}
                                onDragEnter={handleDrag}
                                onDragLeave={handleDrag}
                                onDragOver={handleDrag}
                                onDrop={handleDrop}
                                onClick={() => document.getElementById('project-file-input')?.click()}
                            >
                                <input
                                    id="project-file-input"
                                    type="file"
                                    className="hidden"
                                    accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt,.zip,.rar,.7z"
                                    onChange={handleFileChange}
                                />
                                <div className="w-14 h-14 rounded-2xl bg-emerald-50 text-brand-emerald flex items-center justify-center mx-auto mb-3">
                                    <UploadCloud size={28} />
                                </div>
                                <h4 className="text-base font-extrabold text-gray-800 m-0 mb-1">
                                    Click or Drag & Drop project file to upload
                                </h4>
                                <p className="text-xs text-gray-500 font-semibold m-0">
                                    Supported formats: PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, CSV, TXT, ZIP
                                </p>
                            </div>
                        ) : (
                            <div className="file-selected-card">
                                <div className="flex items-center gap-3.5 min-w-0">
                                    <div className="p-2.5 rounded-xl bg-gray-50 flex items-center justify-center flex-shrink-0">
                                        {getFileIcon(selectedFile.name)}
                                    </div>
                                    <div className="min-w-0">
                                        <h4 className="text-sm font-bold text-gray-900 truncate m-0 mb-0.5">
                                            {selectedFile.name}
                                        </h4>
                                        <p className="text-xs text-gray-500 font-semibold m-0">
                                            {formatFileSize(selectedFile.size)} &bull; Ready for upload
                                        </p>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setSelectedFile(null)}
                                    className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors"
                                    title="Remove file"
                                >
                                    <X size={18} />
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Actions */}
                    <div className="form-actions-bar">
                        <Link
                            to="/instructor/projects"
                            className="btn-cancel-action"
                        >
                            Cancel
                        </Link>
                        <button
                            type="submit"
                            disabled={isSubmitting || !selectedFile || !formData.title.trim()}
                            className="btn-submit-action"
                        >
                            {isSubmitting ? (
                                <>
                                    <Loader2 size={18} className="animate-spin" />
                                    <span>Uploading...</span>
                                </>
                            ) : (
                                <>
                                    <CheckCircle2 size={18} strokeWidth={2.5} />
                                    <span>Publish Project</span>
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
